import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useClubFeatureFlags,
  useRoutesByBrand,
  useStaff,
  useStaffChangeRole,
  useStaffCreate,
  useStaffEdit,
  useStaffList,
  useStaffSetClubAccessible,
} from "@gymflow/api";
import { cn } from "@gymflow/helpers";
import { UserStaffBean } from "@gymflow/types";
import { createColumnHelper } from "@tanstack/react-table";
import { copyToClipboard } from "apps/portal/src/helpers";
import { useIsLargeScreen } from "apps/portal/src/hooks";
import { NonRecursive } from "apps/portal/src/types/nonRecursive";
import { capitalize } from "lodash";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { usePageSize } from "../../../hooks/usePageSize";
import { usePortalRoutes } from "../../../hooks/usePortalRoutes";
import { ModalContext, useClubSettings } from "../../../providers";
import { AuthenticationContext } from "../../../providers/authentication/AuthenticationContext";
import { RouteFeature } from "../../../routes/feature";
import { RouteLayout } from "../../../routes/layout";
import useGymflowModels from "../../../store";
import { Avatar, Badge, Button, CopyIcon, ReactiveButton } from "../../atoms";
import DropdownFilter from "../../DropdownFilter";
import { PaginatedTable } from "../../organisms/PaginatedTable";
import { StaffSidebar } from "../../templates/Staff/components/StaffSidebar/StaffSidebar";
import { StaffFormValues } from "../../templates/Staff/components/StaffSidebar/types";
import { mapEditingToFormValues } from "../../templates/Staff/components/StaffSidebar/utils/helpers";
import { StaffFormModal } from "../../templates/Staff/StaffFormModal";
import { SettingsContainer } from "../SettingsContainer";
import { StaffActionsMenuPopover } from "./StaffActionsMenuPopover";
import { StaffLocationsTags } from "./StaffLocationsTags";

export function StaffTable() {
  const { t } = useTranslation();
  const settings = useClubSettings();
  const { api } = useGymflowModels();
  const { brand, createAbsoluteClubLink } = usePortalRoutes();
  const { data: featureFlags } = useClubFeatureFlags({
    clubId: settings.clubId,
    api,
  });

  const { setModal, hide } = useContext(ModalContext);

  const [editingStaffId, setEditingStaffId] = useState<string>();
  const [isActiveOption, setIsActiveOption] = useState({
    label: "Enabled",
    value: true,
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [existingValues, setExistingValues] = useState<StaffFormValues>();

  const [isModalOpened, setIsModalOpened] = useState(false);

  const { userId: loggedInId, logout } = useContext(AuthenticationContext);

  const [selectedPage, setSelectedPage] = useState(0);
  const [sort, setSort] = useState<{ id: string; desc: boolean }[]>([]);
  const tableContainerRef = useRef(null);
  const pageSize = usePageSize({
    tableContainerRef,
    rowHeight: 56,
  });
  const { data: editing } = useStaff({
    api,
    staffId: editingStaffId ?? undefined,
  });
  const changeRoleMutation = useStaffChangeRole({ api });

  const { data, isLoading } = useStaffList({
    api,
    opts: {
      page: selectedPage,
      limit: pageSize,
      sort: sort?.[0]
        ? {
            field: sort[0].id,
            desc: sort[0].desc,
          }
        : undefined,
      extraParams: { activeUser: isActiveOption.value },
    },
  });
  const editStaff = useStaffEdit({ api });
  const createStaff = useStaffCreate({ api });
  const setClubsAccessibleStaff = useStaffSetClubAccessible({ api });
  const { data: routesData } = useRoutesByBrand({
    brand,
    page: 0,
    limit: 1000000,
  });

  const handleSubmit = useCallback(
    async (values: any) => {
      if (editing) {
        const { clubAccessibleList, role, ...staffFields } =
          values.patchedFields;
        if (Object.keys(staffFields)) {
          await editStaff.mutateAsync({
            staffId: values.id,
            patchedFields: staffFields,
          });
        }

        if (clubAccessibleList) {
          setClubsAccessibleStaff.mutateAsync({
            staffId: values.id,
            accessibleClubs: clubAccessibleList,
          });
        }
        if (role && role !== editing.role) {
          await changeRoleMutation.mutateAsync({
            clubId: settings.clubId,
            staffId: values.id,
            role,
          });

          if (values.id === loggedInId) {
            logout();
          }
        }
      } else {
        const { clubAccessibleList, role, ...staffFields } = values;
        const {
          data: { id },
        } = await createStaff.mutateAsync(staffFields);
        setClubsAccessibleStaff.mutateAsync({
          staffId: id,
          accessibleClubs: clubAccessibleList,
        });
        if (role !== "MANAGER") {
          await changeRoleMutation.mutateAsync({
            clubId: settings.clubId,
            staffId: id,
            role,
          });
        }
      }

      setEditingStaffId(undefined);
      hide();
      setIsModalOpened(false);
    },
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createStaff, editStaff, editing, hide],
  );

  const showFormSidebar = useCallback(() => {
    if (!editing) {
      setExistingValues(undefined);
      setIsSidebarOpen(true);
      return;
    }
    const existingValues = mapEditingToFormValues(editing, routesData?.results);
    setExistingValues(existingValues);
    setIsSidebarOpen(true);
  }, [editing, routesData]);

  const closeFormSidebar = useCallback(() => {
    setEditingStaffId(undefined);
    setExistingValues(undefined);
    setIsSidebarOpen(false);
  }, []);

  const showFormModal = useCallback(() => {
    let existingValues = null;
    if (editing) {
      existingValues = {
        ...editing,
        clubAccessibleList: editing.accessibleClubIds.map((accessClubId) => {
          const clubName = routesData?.results?.find(
            ({ clubId }) => accessClubId === clubId,
          )?.displayName;
          return { id: accessClubId, name: clubName };
        }),
      };
    }
    setIsModalOpened(true);
    setModal(
      <StaffFormModal
        existingValues={existingValues}
        onSubmit={(submittedValues: any) => {
          return handleSubmit(submittedValues);
        }}
        onCancel={() => {
          setEditingStaffId(undefined);
          setModal(null);
          setIsModalOpened(false);
        }}
        showEditingSelfRoleWarning={loggedInId === existingValues?.id}
      />,
    );
  }, [editing, handleSubmit, loggedInId, routesData?.results, setModal]);

  useEffect(() => {
    if (editingStaffId && editing && !isSidebarOpen && !isModalOpened) {
      featureFlags?.featureFlags.PORTAL_NEW_STAFF_SIDEBAR
        ? showFormSidebar()
        : showFormModal();
    }
  }, [
    editing,
    editingStaffId,
    featureFlags?.featureFlags.PORTAL_NEW_STAFF_SIDEBAR,
    isSidebarOpen,
    showFormModal,
    showFormSidebar,
    isModalOpened,
  ]);

  const isLargeScreen = useIsLargeScreen();

  const columnHelper = createColumnHelper<NonRecursive<UserStaffBean>>();
  const columns = [
    columnHelper.accessor(
      (row) => {
        return {
          firstName: row.firstName,
          lastName: row.lastName,
          picture: row.picture,
          email: row.email,
        };
      },
      {
        id: "firstName",
        cell: (info) => {
          const { firstName, lastName, picture, email } = info.getValue();
          return (
            <div className="flex gap-x-4">
              <Avatar url={picture} />
              <div className="flex flex-col">
                <div
                  className={cn(
                    {
                      "text-gray-300": !isActiveOption.value,
                    },
                    "font-semibold",
                  )}
                >{`${firstName} ${lastName}`}</div>
                <div
                  className={cn({
                    "text-gray-600": isActiveOption.value,
                    "text-gray-300": !isActiveOption.value,
                  })}
                >
                  {email}
                </div>
              </div>
            </div>
          );
        },
        header: () => (
          <span>{t("page.settings.tab.staff.table.header.name")}</span>
        ),
        meta: {
          suspense: () => (
            <td className="relative animate-pulse py-4 pr-3 text-sm font-medium text-gray-900">
              <div className="ml-4 flex space-x-4">
                <div>
                  <div className="size-10 rounded-full bg-gray-400"></div>
                </div>
                <div className="flex w-2/5 flex-col space-y-4">
                  <div className="h-2 rounded bg-gray-400"></div>
                  <div className="h-2 rounded bg-gray-400"></div>
                </div>
              </div>
            </td>
          ),
        },
        sortDescFirst: false,
      },
    ),
    columnHelper.accessor("role", {
      cell: (cell) => {
        return (
          <Badge className="rounded-full text-sm font-medium">
            {capitalize(cell.getValue())}
          </Badge>
        );
      },
      enableSorting: false,
    }),
    columnHelper.accessor("mobileNumber", {
      header: () => t("page.settings.tab.staff.table.header.phone"),
    }),
    columnHelper.display({
      id: "locations",
      header: () => t("page.settings.tab.staff.table.header.locations"),
      cell: (cell) => (
        <StaffLocationsTags
          accessibleClubIds={cell.row.original.accessibleClubIds}
        />
      ),
    }),
    ...(isLargeScreen
      ? [
          columnHelper.accessor("id", {
            header: () => t("page.settings.tab.staff.table.header.bookingLink"),
            cell: (column) => (
              <ReactiveButton
                size="small"
                onClick={async () => {
                  await copyToClipboard(
                    createAbsoluteClubLink(
                      RouteLayout.Site,
                      `${RouteFeature.SiteTimetableType.replace(":viewType", "list")}?host=${column.getValue()}`,
                    ),
                  );
                }}
                reactiveContent="Link Copied"
                className="w-32"
              >
                <div className="flex w-full items-center gap-1">
                  <CopyIcon pathClassName="stroke-gray-500" />
                  {t("page.settings.tab.staff.table.copyLink")}
                </div>
              </ReactiveButton>
            ),
          }),
        ]
      : []),
    columnHelper.display({
      id: "actions",
      header: t("page.settings.tab.staff.table.header.actions"),
      cell: (props) => (
        <StaffActionsMenuPopover
          staff={props.row.original}
          setEditingStaffId={setEditingStaffId}
        />
      ),
    }),
  ];

  return (
    <SettingsContainer
      title={t("page.settings.tab.staff.table.title")}
      subTitle={t("page.settings.tab.staff.table.subTitle")}
      actions={
        <>
          <DropdownFilter
            className="mt-0"
            options={[
              {
                label: t("page.settings.tab.staff.table.dropdown.enabled"),
                value: true,
              },
              {
                label: t("page.settings.tab.staff.table.dropdown.disabled"),
                value: false,
              },
            ]}
            value={isActiveOption}
            onChange={(v: any) => setIsActiveOption(v)}
          />
          <Button
            intent="secondary"
            onClick={
              featureFlags?.featureFlags.PORTAL_NEW_STAFF_SIDEBAR
                ? showFormSidebar
                : showFormModal
            }
          >
            <div className="flex flex-row items-center gap-x-2">
              <FontAwesomeIcon icon={faPlus} />
              {t("page.settings.tab.staff.table.addStaff")}
            </div>
          </Button>
        </>
      }
    >
      <PaginatedTable
        tableProps={{
          data: data?.content ?? [],
          columns: columns,
          pageCount: data?.totalPages,
          pageIndex: data?.number,
          onSortingChange: setSort,
          sort: sort,
          isFetching: isLoading,
          pageSize: pageSize,
          tableContainerRef,
        }}
        hasNextPage={!!data && data?.number < data?.totalPages - 1}
        hasPreviousPage={!!data && data?.number > 0}
        goToNextPage={() => {
          setSelectedPage((e) => e + 1);
        }}
        goToPreviousPage={() => {
          setSelectedPage((e) => e - 1);
        }}
      />
      {featureFlags?.featureFlags.PORTAL_NEW_STAFF_SIDEBAR ? (
        <StaffSidebar
          existingValues={existingValues}
          editingStaffId={editingStaffId}
          setEditingStaffId={setEditingStaffId}
          onClose={closeFormSidebar}
          isOpen={isSidebarOpen}
        />
      ) : null}
    </SettingsContainer>
  );
}
